'use client';
import { webNotificationBannerStore } from '#pplx/frontend/apps/ask/hooks/stream-hooks/webNotificationBannerStore.js';
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { getPushPermission, isPushEnabled, isPushSupported, enablePush, } from '#pplx/frontend/push-notifications/pushNotifications.js';
import { streamEvents } from '#pplx/frontend/stream/events.js';
import { isSubmitParams } from '#pplx/frontend/stream/utils.js';
import { useCallback, useEffect } from 'react';
const IN_THREAD_COOLDOWN_MS = 24 * 60 * 60 * 1000;
/**
 * Storage key for the push prompt cooldown. Intentionally NOT under the
 * `pplx.local-user-settings.` prefix so it survives sign-in/sign-out cycles
 * — the cooldown is per-browser, not per-user.
 */
const PUSH_PROMPT_STORAGE_KEY = 'pplx.pushPromptLastShownAt';
// Per-browser cooldown (see key comment above): keep it un-namespaced under multi-account
// scope-by-default so it stays shared across accounts and survives sign-in/sign-out.
function getLastShownAt() {
    const raw = SafeLocalStorage.getItem(PUSH_PROMPT_STORAGE_KEY, { global: true });
    if (raw === null)
        return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
}
function setLastShownAt(timestamp) {
    SafeLocalStorage.setItem(PUSH_PROMPT_STORAGE_KEY, String(timestamp), { global: true });
}
export function clearCooldown() {
    SafeLocalStorage.removeItem(PUSH_PROMPT_STORAGE_KEY, { global: true });
}
function hasGrantedNotificationPermission() {
    return getPushPermission() === 'granted';
}
export function usePushNotificationPrompt() {
    const maybePromptForPush = useCallback(async () => {
        if (!isPushSupported())
            return;
        try {
            if (await isPushEnabled())
                return;
        }
        catch {
            return;
        }
        const lastShownAt = getLastShownAt();
        if (lastShownAt > 0 && Date.now() - lastShownAt < IN_THREAD_COOLDOWN_MS)
            return;
        setLastShownAt(Date.now());
        if (hasGrantedNotificationPermission()) {
            await enablePush({ reason: 'push-prompt-permission-restore' });
            return;
        }
        // The banner may not be mounted yet (e.g. before navigation lands on the
        // thread page), but the store flip persists until it reads it on mount.
        webNotificationBannerStore.show();
    }, []);
    useEffect(() => {
        const unsub = streamEvents.on('created', ({ params }) => {
            if (!isSubmitParams(params))
                return;
            if (params.query_source !== 'computer' &&
                params.query_source !== 'computer-workflow' &&
                params.query_source !== 'computer-followup' &&
                params.query_source !== 'computer-voice')
                return;
            maybePromptForPush();
        });
        return unsub;
    }, [maybePromptForPush]);
}
//# sourceMappingURL=usePushNotificationPrompt.js.map